import { LucideIcon } from "lucide-react";
import { useInView } from "@/hooks/useInView";

interface ProcessStep {
  icon: LucideIcon;
  title: string;
  description: string;
  gradient: string;
}

interface ProcessTimelineProps {
  badge: { icon: LucideIcon; text: string; color: string };
  title: string;
  subtitle?: string;
  steps: ProcessStep[];
}

export const ProcessTimeline = ({ badge, title, subtitle, steps }: ProcessTimelineProps) => {
  const { ref, isInView } = useInView({ threshold: 0.2 });
  const BadgeIcon = badge.icon;

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; text: string; border: string }> = {
      blue: { bg: "bg-blue-100", text: "text-blue-600", border: "border-blue-200" },
      purple: { bg: "bg-purple-100", text: "text-purple-600", border: "border-purple-200" },
      green: { bg: "bg-green-100", text: "text-green-600", border: "border-green-200" },
      orange: { bg: "bg-orange-100", text: "text-orange-600", border: "border-orange-200" },
      pink: { bg: "bg-pink-100", text: "text-pink-600", border: "border-pink-200" },
      cyan: { bg: "bg-cyan-100", text: "text-cyan-600", border: "border-cyan-200" },
    };
    return colors[color] || colors.blue;
  };

  const badgeColors = getColorClasses(badge.color);

  return (
    <section ref={ref} className="py-12 md:py-20 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6">
        <div className="text-center mb-10 md:mb-16">
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 rounded-full ${badgeColors.bg} ${badgeColors.text} border ${badgeColors.border} mb-4 md:mb-6`}>
            <BadgeIcon className="w-3.5 h-3.5 md:w-4 md:h-4" />
            <span className="text-xs md:text-sm font-medium">{badge.text}</span>
          </div>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-3 md:mb-4">{title}</h2>
          {subtitle && (
            <p className="text-base md:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto px-2">{subtitle}</p>
          )}
        </div>

        <div className="relative max-w-5xl mx-auto">
          {/* Timeline line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-primary/20 via-primary/40 to-primary/20 rounded-full hidden lg:block" />

          <div className="space-y-12 lg:space-y-0">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isLeft = index % 2 === 0;

              return (
                <div
                  key={index}
                  className={`relative flex items-center lg:h-32 transition-all duration-700 ${
                    isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
                  }`}
                  style={{ transitionDelay: `${index * 150}ms` }}
                >
                  {/* Desktop layout */}
                  <div className="hidden lg:flex w-full items-center">
                    {isLeft ? (
                      <>
                        <div className="w-5/12 pr-8 text-right">
                          <h3 className="text-xl font-bold text-foreground mb-2">{step.title}</h3>
                          <p className="text-muted-foreground">{step.description}</p>
                        </div>
                        <div className="w-2/12 flex justify-center">
                          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${step.gradient} flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform duration-300`}>
                            <StepIcon className="w-8 h-8 text-white" />
                          </div>
                        </div>
                        <div className="w-5/12 pl-8">
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-bold text-sm">
                            {index + 1}
                          </span>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="w-5/12 pr-8 text-right">
                          <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-bold text-sm">
                            {index + 1}
                          </span>
                        </div>
                        <div className="w-2/12 flex justify-center">
                          <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${step.gradient} flex items-center justify-center shadow-lg transform hover:scale-110 transition-transform duration-300`}>
                            <StepIcon className="w-8 h-8 text-white" />
                          </div>
                        </div>
                        <div className="w-5/12 pl-8">
                          <h3 className="text-xl font-bold text-foreground mb-2">{step.title}</h3>
                          <p className="text-muted-foreground">{step.description}</p>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Mobile layout */}
                  <div className="lg:hidden flex items-start gap-4 w-full">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${step.gradient} flex items-center justify-center shadow-lg flex-shrink-0`}>
                      <StepIcon className="w-7 h-7 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary font-bold text-xs">
                          {index + 1}
                        </span>
                        <h3 className="text-lg font-bold text-foreground">{step.title}</h3>
                      </div>
                      <p className="text-muted-foreground text-sm">{step.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
